import React, { useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { OrderedMap, Record } from 'immutable';
import { useHistory } from 'react-router';
import { v4 as uuidv4 } from 'uuid';
import { useToggle } from 'Shared/utils/hooks';

// Types
import { GoalItem } from 'Shared/constants/types';

// Selectors
import * as UsersSelectors from 'Shared/selectors/Users.selectors';
import { getUserFlag } from 'Shared/selectors/user';

// Actions
import * as UsersActions from 'Shared/actions/Users.actions';

// Utils
import PageUrlsUtils from 'Shared/utils/PageUrls.utils';
import { trackUsedGoalFilter } from 'Shared/tracking/tracking';

// Actions
import { markGoalIncomplete, markGoalComplete, changeStatus } from 'Shared/actions/Goals.actions';

// Components
import WpTextField from 'Wpds/components/text-field/WpTextField';
import WpIcon from 'Wpds/components/icon/WpIcon';
import { WpToggleButtonGroup, WpToggleButton } from 'Wpds/components/toggle-button';
import GoalTable, { GoalTableColumns } from './GoalsTable/GoalsTable';
import SelectedFilters from './SelectedFilters/SelectedFilters';
import HideColumnDropdown from './GoalsTable/partials/HideColumnDropdown';
import CompleteGoalModal from './CompleteGoalModal/CompleteGoalModal';
import FiltersContainer from './Filters/FiltersContainer';
import GoalsCascadingTableView from './GoalsCascadingTableView/GoalsCascadingTableView';

// Styles
import './GoalsContainer.scss';

enum ViewAsType {
  table = 'table',
  grid = 'grid',
  cascade = 'cascade',
}
const DEFAULT_VIEW_AS_TYPE = ViewAsType.cascade;

type Props = {
  goals: OrderedMap<string, Record<GoalItem>>;
  loading?: boolean;
  viewAs?: ViewAsType;
  onItemClick?: Function;
};

const GoalsContainer = ({ goals, viewAs = DEFAULT_VIEW_AS_TYPE, onItemClick }: Props) => {
  const dispatch = useDispatch();
  const history = useHistory();

  const users = useSelector(state => UsersSelectors.getAllUsers(state));
  const goalTableViewSettings = useSelector(state => getUserFlag(state, { flag: 'goals_table_view_settings' }));

  const [activeTab, setActiveTab] = useState(viewAs);
  const [textSearchInput, setTextSearchInput] = useState('');
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [filteredGoals, setFilteredGoals] = useState([]);
  const [hiddenColumns, setHiddenColumns] = useState([]);
  const [settingsLoaded, setSettingsLoaded] = useState(false);
  const [markCompleteGoal, setMarkCompleteGoal] = useState(null);
  const [сompleteGoalModalIsOpen, completeGoalModalIsOpenToggle] = useToggle(false);

  const isCascadeView = activeTab === ViewAsType.cascade;

  const handleAddFilter = filter => {
    const uuid = uuidv4();
    setSelectedFilters([...selectedFilters, { uuid, ...filter }]);
    if (filter.type === 'owner') {
      trackUsedGoalFilter(filter.type);
    } else {
      trackUsedGoalFilter(filter.type, filter.label);
    }
  };

  const handleRemoveFilter = filterToRemove => {
    setSelectedFilters(selectedFilters.filter(filter => filter.uuid !== filterToRemove.uuid));
  };

  const handleMarkCompleteClick = goalSlug => {
    setMarkCompleteGoal(filteredGoals.find(goal => goal.slug === goalSlug));
    completeGoalModalIsOpenToggle();
  };

  const handleMarkComplete = useCallback(
    ({ goalSlug, closeActionItems, resolutionNote }) => {
      dispatch(markGoalComplete({ closeActionItems, goalSlug, resolutionNote }));
    },
    [dispatch]
  );

  const handleMarkIncomplete = useCallback(
    slug => {
      dispatch(markGoalIncomplete(slug));
    },
    [dispatch]
  );

  const handleChangeStatus = useCallback(
    (slug, status) => {
      dispatch(changeStatus(slug, status));
    },
    [dispatch]
  );

  const redirectToGoalDetailPage = useCallback(
    slug => {
      history.push({
        pathname: PageUrlsUtils.makeGoalDetailPanelUrl(slug),
        // state: { modal: true },
      });
    },
    [history]
  );

  useEffect(() => {
    // first time user viewing goals table, add an initial configuration
    if (!goalTableViewSettings) {
      handleAddFilter({ type: 'completion', label: 'Open Goals', completionId: 'incomplete' });
      setHiddenColumns([GoalTableColumns.workspace]);
    } else {
      setHiddenColumns(
        goalTableViewSettings.hiddenColumns
          ? goalTableViewSettings.hiddenColumns.map(hc => hc.toLowerCase() as GoalTableColumns)
          : []
      );
      setSelectedFilters(goalTableViewSettings.selectedFilters || []);

      if (goalTableViewSettings?.activeTab === ViewAsType.grid || viewAs === ViewAsType.grid) {
        setActiveTab(ViewAsType.cascade);
      } else setActiveTab(goalTableViewSettings.activeTab || viewAs);
    }
    setSettingsLoaded(true);
  }, []);

  useEffect(() => {
    if (settingsLoaded) {
      dispatch(UsersActions.updateUserFlag('goals_table_view_settings', { hiddenColumns, activeTab, selectedFilters }));
    }
  }, [hiddenColumns, activeTab, selectedFilters]);

  useEffect(() => {
    setFilteredGoals(
      goals
        .filter(goal => goal.get('name').toLocaleLowerCase().includes(textSearchInput.toLocaleLowerCase()))
        .filter(goal => {
          const ownerFitlers = selectedFilters.filter(filter => filter.type === 'owner');
          if (ownerFitlers.length === 0) return true;
          const ownerIds = ownerFitlers.map(owner => owner.ownerId);
          return ownerIds.indexOf(goal.get('assigneeId')) > -1;
        })
        .filter(goal => {
          const statusFilters = selectedFilters.filter(filter => filter.type === 'status');
          if (statusFilters.length === 0) return true;
          const statusTypes = statusFilters.map(status => status.statusId);
          return statusTypes.indexOf(goal.get('status')) > -1;
        })
        .filter(goal => {
          const departmentFilters = selectedFilters.filter(filter => filter.type === 'department');
          if (departmentFilters.length === 0) return true;
          const departmentIds = departmentFilters.map(filter => filter.departmentId);
          return departmentIds.indexOf(users.getIn([goal.get('assigneeId'), 'department'])) > -1;
        })
        .filter(goal => {
          const completionFilters = selectedFilters.filter(filter => filter.type === 'completion');
          if (completionFilters.length === 0) return true;
          const completionTypes = completionFilters.map(status => status.completionId);
          const goalCompletionValue = goal.get('complete') ? 'complete' : 'incomplete';
          return completionTypes.indexOf(goalCompletionValue) > -1;
        })
        .valueSeq()
        .toJS()
    );
  }, [goals, selectedFilters, textSearchInput, activeTab]);

  const hasFilters = selectedFilters.length > 0;

  const baseClassName = 'GoalsContainer';

  return (
    <div className={baseClassName}>
      <div className={`${baseClassName}__toolbar`}>
        <div className={`${baseClassName}__toolbar__search`}>
          <WpTextField
            id="goals-search-input"
            label="Search Goals"
            placeholder="Search Goals"
            labelHidden
            prefixIcon="search"
            value={textSearchInput}
            onChange={setTextSearchInput}
          />
        </div>
        <div className={`${baseClassName}__toolbar__filters`}>
          <FiltersContainer
            handleAddFilter={handleAddFilter}
            selectedFilters={selectedFilters}
            handleRemoveFilter={handleRemoveFilter}
          />
        </div>
        <div className={`${baseClassName}__toolbar__right-actions`}>
          {!isCascadeView && <HideColumnDropdown hiddenColumns={hiddenColumns} setHiddenColumns={setHiddenColumns} />}
          <div className={`${baseClassName}__toolbar__right-actions__view-toggle`}>
            <WpToggleButtonGroup activeTab={activeTab} onTabClick={setActiveTab}>
              <WpToggleButton tabId={ViewAsType.cascade}>
                <WpIcon prefix="fac" name="nested-link" />
              </WpToggleButton>
              <WpToggleButton tabId={ViewAsType.table}>
                <WpIcon prefix="fac" name="table" />
              </WpToggleButton>
            </WpToggleButtonGroup>
          </div>
        </div>
      </div>
      {hasFilters && (
        <div className={`${baseClassName}__selected-filters`}>
          <SelectedFilters selectedFilters={selectedFilters} removeFilter={handleRemoveFilter} />
        </div>
      )}
      <div className={`${baseClassName}__content`}>
        {activeTab === ViewAsType.table && (
          <>
            <GoalTable
              goals={filteredGoals}
              handleMarkComplete={handleMarkCompleteClick}
              handleMarkIncomplete={handleMarkIncomplete}
              handleChangeStatus={handleChangeStatus}
              redirectToGoalDetailPage={redirectToGoalDetailPage}
              onRowClick={onItemClick}
              hiddenColumns={hiddenColumns}
              windowedScrolling
              maxHeight="calc(100vh - 209px)"
            />
            {сompleteGoalModalIsOpen && (
              <CompleteGoalModal
                goalSlug={markCompleteGoal.slug}
                goalResolutionNote={markCompleteGoal.resolutionNote}
                onSubmit={handleMarkComplete}
                onToggle={completeGoalModalIsOpenToggle}
              />
            )}
          </>
        )}
        {activeTab === ViewAsType.cascade && (
          <GoalsCascadingTableView
            goals={filteredGoals}
            onItemClick={onItemClick}
            allowOrphaned={true}
            windowedScrolling
            maxHeight="calc(100vh - 209px)"
          />
        )}
      </div>
    </div>
  );
};

export default GoalsContainer;
